import { VFC, useContext, useMemo } from 'react'
import type { Row } from 'react-table'
import { OBJECT } from '../../../../../common/utils/Utils'
import { DeleteIcon, LockIcon, PencilIcon } from '@panwds/icons'
import { Button, Tooltip } from '@panwds/react-ui'
import GlobalContext from '../../../../../common/context/GlobalContext'
import {
  TOOLTIP_INHERITED_DELETE,
  TOOLTIP_INHERITED_SERVICE_ACCOUNT_DELETE,
  TOOLTIP_HIGHLANDER_SUPERUSER, TOOLTIP_READ_ONLY
} from '../../../../constants/LabelConstants'
import useReadOnly from '../../../../../common/hooks/useReadOnly'
import { IDENTITIES_PERMISSION_SET } from '../../../../constants/IAMConstants'

const ActionsRenderer: VFC<{
  row: Row<any>,
  onEditClick: Function,
  onDeleteClick: Function,
  onRemoveServiceAccount: Function
}> = ({ row, onDeleteClick, onEditClick, onRemoveServiceAccount }) => {

  const { treeReducer, iamReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { policiesMap } = iamReducer
  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)

  const editButton = useMemo(() => {
    return readOnly ? (
      <Tooltip label={TOOLTIP_READ_ONLY}>
        <div className='cm-flex cm-justify-center cm-items-center cm-grayscale cm-text-gray' style={{ height: '2rem', width: '2rem' }}>
          <PencilIcon className='cm-text-gray-300'/>
        </div>
      </Tooltip>
    ) : (
      <Button
        disabled={readOnly}
        onClick={() => onEditClick(row?.original)}
        appearance='tertiary-clear'
        aria-label='iam-edit-user'
        addClassName='cm-grayscale cm-text-gray'
        icon={<PencilIcon className='cm-text-gray-500'/>}
      />
    )
  }, [onEditClick, readOnly, row?.original])

  const highlander = useMemo(() => {
    const superusers = []
    for (const policies of Object.values(policiesMap)) {
      for (const policy of policies) {
        const { role, inherited_from, resource } = policy
        if (
          inherited_from === selectedTreeNode.id &&
          (resource as any)?.app_id === '' &&
          ['msp_superuser', 'superuser'].includes(role)
        ) {
          superusers.push(policy)
        }
      }
    }
    if (!superusers.some(policy => policy.principal === row.original.identity_email)) {
      return false
    }
    return superusers.length <= 1
  }, [policiesMap, row.original.identity_email, selectedTreeNode.id])

  return (
    <div className='cm-relative cm-flex-1'>
      {OBJECT.hasObject(row?.original?.roles) ? Object.entries(row?.original?.roles).sort().map((([app_id, policies], app_index) => {
        return (policies as any)?.map((policy: any, index: any) => {
          const { inherited_from, resource, role } = policy
          const isTop = index === 0 && app_index === 0
          const isInherited = inherited_from !== selectedTreeNode?.id
          const isOnlyOne = highlander && resource.app_id === '' && ['msp_superuser', 'superuser'].includes(role)
          return (
            <div key={index} className='cm-flex-1 cm-flex cm-gap-2'>
              {isTop ? (
                <>
                  {editButton}
                </>
              ) : (
                <div style={{ height: '2rem', width: '2rem' }}/>
              )}
              {readOnly ? (
                <Tooltip label={TOOLTIP_READ_ONLY}>
                  <div className='cm-flex cm-justify-center cm-items-center' style={{ height: '2rem', width: '2rem' }}>
                    <DeleteIcon className='cm-text-gray-300'/>
                  </div>
                </Tooltip>
              ) : isInherited ? (
                <Tooltip label={TOOLTIP_INHERITED_DELETE}>
                  <div className='cm-flex cm-justify-center cm-items-center' style={{ height: '2rem', width: '2rem' }}>
                    <LockIcon className='cm-text-gray-500 cm-opacity-60'/>
                  </div>
                </Tooltip>
              ) : isOnlyOne ? (
                <Tooltip label={TOOLTIP_HIGHLANDER_SUPERUSER}>
                  <div className='cm-flex cm-justify-center cm-items-center' style={{ height: '2rem', width: '2rem' }}>
                    <LockIcon className='cm-text-gray-500'/>
                  </div>
                </Tooltip>
              ) : (
                <Button
                  onClick={() => onDeleteClick(row?.original, policy)}
                  appearance='tertiary-clear'
                  aria-label='iam-delete-user'
                  addClassName='cm-grayscale cm-p-1'
                  icon={<DeleteIcon className='cm-text-gray-500'/>}
                />
              )}
            </div>
          )
        })
      })) : (
        <div className='cm-flex-1 cm-flex cm-gap-2'>
          {editButton}
          {row?.original?.tsg_id === selectedTreeNode?.id ? (
            <Button
              onClick={() => onRemoveServiceAccount(row?.original)}
              appearance='tertiary-clear'
              aria-label='iam-delete-user'
              addClassName='cm-grayscale cm-text-gray cm-p-1'
              icon={<DeleteIcon className='cm-text-gray-500'/>}
            />
          ) : (
            <Tooltip label={TOOLTIP_INHERITED_SERVICE_ACCOUNT_DELETE}>
              <div className='cm-flex cm-justify-center cm-items-center' style={{ height: '2rem', width: '2rem' }}>
                <LockIcon className='cm-text-gray-500'/>
              </div>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}

export default ActionsRenderer
